// src/Functions/PendingDestination.js
//
// T2f — remember where someone was going when RouteGuard bounced them to
// sign-in, and send them there once they are authenticated.
//
// Confirmed behaviour before this existed (2026-09-03): opening
// /business/:id/social/accounts while signed out lands on the sign-in page,
// and signing in lands on /home. The integrations page renders correctly when
// already authenticated, so the page was never the problem — the guard
// discarding the destination was.
//
// WHY sessionStorage RATHER THAN location.state.
//
// The existing redirect mechanism passes location.state.link through
// navigate(). That works for movement INSIDE the SPA, but it cannot carry
// this case:
//
//   - a connect link arrives from an email or the token review page, which is
//     a cold page load with no prior navigate() call, so location.state is
//     empty;
//   - signInWithRedirect({ provider: 'Google' }) is a full-page round trip to
//     Cognito and back, which discards in-memory router state entirely.
//
// sessionStorage is same-origin and survives both. It is also per-tab, so a
// destination stored in one tab cannot hijack another.
//
// The key is namespaced and does not collide with the OAuth flow's own
// sessionStorage use (oauth_platform, oauth_business_id, oauth_return_path and
// friends). OAuthCallback's clearSession() removes an explicit list of keys,
// not a prefix, so it cannot clear this one either.
//
// EXPIRY IS DELIBERATE. Without it, someone bounced to sign-in on Monday and
// returning on Friday would be thrown at a stale page for no visible reason.
// Ten minutes covers an OAuth round trip and a slow password reset, and
// nothing else.

const KEY = 'revfer_pending_destination';
const TTL_MS = 10 * 60 * 1000;

// Never send someone back to an auth screen — that loops.
const NEVER_RETURN_TO = ['/auth/signin', '/auth/signout', '/'];

// Paths that are guarded but must NEVER be replayed.
//
// /oauth/callback/:platform sits behind RouteGuard like everything else, so if
// the Cognito session lapses during the round trip to Facebook or Google, the
// guard would otherwise store the callback URL and send the person back to it
// after signing in. That cannot work: the `code` query parameter is
// single-use, and the matching oauth_states record expires after 15 minutes.
// They would land on "Connection Failed" instead of the sign-in page they
// would otherwise have got — a worse outcome than storing nothing.
//
// Matched by prefix because the platform segment and query string vary.
const NEVER_STORE_PREFIXES = ['/oauth/callback'];

function isStorable(path) {
  if (!path) return false;
  // Compare the path only; a query string must not defeat the check.
  const bare = path.split('?')[0];
  if (NEVER_RETURN_TO.includes(bare)) return false;
  if (NEVER_STORE_PREFIXES.some(p => bare.startsWith(p))) return false;
  return true;
}

export function setPendingDestination(path) {
  if (!isStorable(path)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Private browsing, storage disabled, quota. Losing the destination means
    // the person lands on /home instead — degraded, not broken.
  }
}

/**
 * Read and REMOVE the stored destination. Removing on read is what stops a
 * destination firing twice, and what stops one surviving into an unrelated
 * later sign-in.
 *
 * @returns {string|null} the path, or null if absent, expired or unreplayable
 */
export function takePendingDestination() {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    sessionStorage.removeItem(KEY);

    const { path, at } = JSON.parse(raw);
    if (!path || typeof at !== 'number') return null;
    if (Date.now() - at > TTL_MS) return null;

    // Re-checked on the way out as well as on the way in: a value written by
    // an older build, or by hand, still must not be replayed.
    if (!isStorable(path)) return null;

    return path;
  } catch {
    return null;
  }
}

/** Drop it without navigating. Used when the person is routed elsewhere. */
export function clearPendingDestination() {
  try { sessionStorage.removeItem(KEY); } catch { /* nothing to do */ }
}